import React, { useState, useCallback, useEffect } from 'react';
import Select from 'react-select/async';
import { Button, Input, FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import debounce from 'lodash/debounce';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const MAX_RADIUS = 10000;
const DEFAULT_RADIUS = 5000;
const MIN_RADIUS = 100;

const CitySelector = ({ isOpen, toggle, onCitySelect }) => {
  const [selectedCity, setSelectedCity] = useState(null);
  const [radius, setRadius] = useState("");

  const loadCityOptions = async (inputValue) => {
    if (!inputValue) return [];
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(inputValue)}&format=json`
      );
      const data = await response.json();
      
      return data
        .filter(item => item.addresstype === 'city')
        .map(city => ({
          value: {
            lat: parseFloat(city.lat),
            lon: parseFloat(city.lon),
            display_name: city.display_name
          },
          label: city.display_name
        }));
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    }
  };

  const debouncedLoadCityOptions = useCallback(
    debounce((inputValue, callback) => {
      loadCityOptions(inputValue).then(callback);
    }, 300),
    []
  );

  
  useEffect(() => {
    return () => {
      debouncedLoadCityOptions.cancel();
    };
  }, [debouncedLoadCityOptions]);

  const handleCitySelect = (option) => {
    setSelectedCity(option);
  };

  const handleRadiusChange = (e) => {
    const value = e.target.value;
    setRadius(value);
  };

  const handleSubmit = () => {
    if (selectedCity) {
      const radiusValue = Number(radius);
      if (radiusValue < MIN_RADIUS || radiusValue > MAX_RADIUS || isNaN(radiusValue)) {
        toast.error(`Radius cannot be larger than 10,000 meters!`);
        return;
      }
      
      onCitySelect({
        lat: selectedCity.value.lat,
        lon: selectedCity.value.lon,
        meter: radiusValue,
        display_name: selectedCity.value.display_name
      });
      toggle();
      setSelectedCity(null);
      setRadius("");
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <Modal isOpen={isOpen} toggle={toggle} size="sm">
        <ModalHeader toggle={toggle}>Select City</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="citySelect">Search City</Label>
            <Select
              id="citySelect"
              value={selectedCity}
              onChange={handleCitySelect}
              loadOptions={debouncedLoadCityOptions}
              placeholder="Type to search cities..."
              isSearchable={true}
              cacheOptions
              defaultOptions={[]}
              isClearable
            />
          </FormGroup>
          <FormGroup>
            <Label for="radiusInput">Radius (meters)</Label>
            <Input
              type="number"
              id="radiusInput"
              value={radius}
              onChange={handleRadiusChange}
              min={MIN_RADIUS}
              max={MAX_RADIUS}
            />
            <small className="text-muted">
              Range: {MIN_RADIUS}m - {MAX_RADIUS/1000}km
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>Cancel</Button>
          <Button color="primary" onClick={handleSubmit} disabled={!selectedCity}>
            Add Location
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CitySelector;